/**
 * Centralized API-failure reporting — the single replacement for the
 * per-fetcher `catch { console.error(...); throw error; }` blocks that used
 * to be pasted across the services layer.
 *
 * Attach AFTER the silent-auth 401 retry interceptor so this only sees
 * failures that survived recovery (a replayed-and-succeeded 401 never lands
 * here). Policy:
 * - Cancellations are not failures and are dropped before anything is
 *   recorded — including the ones that surface as `ERR_NETWORK` because the
 *   abort raced the transport (see `isAbortedRequestFailure`).
 * - Neither is a request the *browser* killed. A response-less failure during
 *   page teardown or with no connectivity is dropped just as silently (see
 *   `isUnreportableTransportFailure`): one teardown kills every in-flight
 *   request at once, so capturing them fans a single non-event out across every
 *   endpoint the page had open.
 * - Every unrecovered failure records bounded, sanitized API diagnostics.
 * - 5xx responses **against our own API** are reported to Sentry as
 *   `ApiRequestError`, grouped per method + route family + status — the API
 *   answered and it was broken, and each endpoint is separately actionable.
 *   Their synthetic exception repeats only the routing summary; the preceding
 *   breadcrumb holds the sanitized evidence.
 * - A response-less own-API failure that survives the guards is a different
 *   claim — "this user could not reach us" — and is reported as
 *   `ApiUnreachableError` at `warning`, in ONE group per error code rather than
 *   per route family. It is still visible if it spikes, without diluting the
 *   handful of real 5xx or paging on ambient browser teardown.
 * - This interceptor is attached to the global axios instance, so it also sees
 *   the third-party lookups the app makes through it. Those are split on
 *   whether the vendor answered (see `resolveThirdPartyVendor`): a 5xx becomes
 *   a `ThirdPartyRequestError` at warning level, while a response-less failure
 *   is breadcrumb-only because it cannot be told apart from an ad blocker.
 *   Neither is ever an `ApiRequestError`, which keeps that name meaning "our
 *   API is broken".
 * - 4xx responses create breadcrumbs but not exceptions: callers surface them
 *   through React Query `onError`/toasts as expected control flow.
 * - Each rejected error is reported once, however many times it crosses this
 *   interceptor (a failed 401 replay traverses it on both the inner and the
 *   outer chain).
 */
import * as Sentry from "@sentry/react";
import { type AxiosError, type AxiosInstance, isAxiosError } from "axios";
// Leaf imports, not the `@/utils` barrel, for two reasons. `sentryRouteError`
// delegates here (see `captureClientRouteError`) and is itself re-exported by
// that barrel, so importing it would close a real ESM cycle. And `authLoader`
// imports this module at module scope, putting it in the entry chunk — a barrel
// import would drag the whole utils directory in behind it.
//
// This holds only while every module in this one's dependency closure also
// avoids the barrel, which is too load-bearing to leave to a comment: biome's
// `noRestrictedImports` override forbids `@utils` in this file, in
// `authOriginGuard`, and across `src/common/utils/**`.
import { isBrowserOffline, isPageUnloading } from "@/utils/pageLifecycle";
import { isRequestAbortError } from "@/utils/requestAbort";
import {
  getSafeAxiosDiagnostics,
  getSafeErrorDiagnostics,
  SAFE_CONSOLE_REDACTED,
  safeConsole,
  sanitizeDiagnosticString,
} from "@/utils/safeConsole";
import { hasObjectType } from "@/utils/typePredicates";
import { resolveThirdPartyVendor } from "./authOriginGuard";

/**
 * Errors already reported. The silent-auth interceptor replays a recovered 401
 * on the same instance, so a failed replay rejects with one AxiosError that
 * passes this reporter twice — once on the inner request, once on the outer
 * chain. Keyed by identity (not by content) and weak, so it never keeps a
 * failed request's payload alive.
 */
const reportedErrors = new WeakSet<object>();

export type TerminalAuthRecoveryOutcome =
  | "reauth_unavailable"
  | "replay_unauthorized";

/** Normalized route family for Sentry grouping. */
const DYNAMIC_ROUTE_SEGMENT = ":id";
const MAX_ROUTE_FAMILY_SEGMENTS = 8;
const MAX_ROUTE_FAMILY_SEGMENT_LENGTH = 64;
const MAX_ROUTE_PERCENT_DECODE_PASSES = 3;
// Legacy `/c/<slug>` company prefix (see `stripCompanyPrefix`): the slug is a
// customer identifier, so it is normalized rather than grouped on.
const COMPANY_SLUG_PREFIX = "c";
const UUID_SEGMENT = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const OBJECT_ID_SEGMENT = /^[0-9a-f]{24}$/i;
const NUMERIC_SEGMENT = /^\d+$/;
const PREFIXED_ID_SEGMENT =
  /^[A-Za-z][A-Za-z0-9._~-]*[-._~]\d[A-Za-z0-9._~-]*$/;
// Opaque operational IDs such as `phone-session-018fe4d3` or `proposal-123`:
// long enough to be an identifier and carrying a digit, which static route
// segments (`overview`, `url-inspection`, `servicetitan`, `me`) do not.
const OPAQUE_ID_SEGMENT = /^(?=[^\s/]*\d)[A-Za-z0-9._~-]{8,}$/;
const PERCENT_ENCODED_ROUTE_BYTE = /%[A-Fa-f0-9]{2}/;

const decodeRouteSegment = (segment: string): string => {
  let decoded = segment;
  for (let pass = 0; pass < MAX_ROUTE_PERCENT_DECODE_PASSES; pass += 1) {
    if (!PERCENT_ENCODED_ROUTE_BYTE.test(decoded)) return decoded;
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return segment;
    }
  }
  return decoded;
};

const canonicalRouteSegment = (segment: string): string => {
  try {
    return encodeURIComponent(decodeRouteSegment(segment));
  } catch {
    return segment;
  }
};

const isDynamicSegment = (segment: string, previous: string): boolean => {
  const decoded = decodeRouteSegment(segment);
  return (
    segment === SAFE_CONSOLE_REDACTED ||
    decodeRouteSegment(previous) === COMPANY_SLUG_PREFIX ||
    decoded.length > MAX_ROUTE_FAMILY_SEGMENT_LENGTH ||
    UUID_SEGMENT.test(decoded) ||
    OBJECT_ID_SEGMENT.test(decoded) ||
    NUMERIC_SEGMENT.test(decoded) ||
    PREFIXED_ID_SEGMENT.test(decoded) ||
    OPAQUE_ID_SEGMENT.test(decoded)
  );
};

/**
 * Collapse an already-sanitized path to its route family: the shape shared by
 * every request to the same endpoint, with operational and customer
 * identifiers normalized away. Grouping only — the full sanitized path stays in
 * the breadcrumb and the event context.
 */
const toRouteFamily = (path: string): string => {
  const [pathname = ""] = path.split("?");
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  const family = segments
    .slice(0, MAX_ROUTE_FAMILY_SEGMENTS)
    .map((segment, index) =>
      isDynamicSegment(segment, segments[index - 1] ?? "")
        ? DYNAMIC_ROUTE_SEGMENT
        : canonicalRouteSegment(segment),
    );
  if (segments.length > MAX_ROUTE_FAMILY_SEGMENTS) family.push("…");
  return `/${family.join("/")}`;
};

/**
 * A cancellation that lost the race to the network layer.
 *
 * `isRequestAbortError` recognizes the clean path: axios rejects with a
 * `CanceledError` when the signal's abort listener runs first. But axios' XHR
 * adapter guards `request.onerror` and that abort listener on the same
 * `request` handle and nulls it after whichever fires first (axios 1.18.0
 * `lib/adapters/xhr.js`), so a browser-level teardown in the same tick rejects
 * with a bare `ERR_NETWORK` even though the cancellation was ours. Only the
 * request's own signal tells the two apart — which is why a fetcher that can be
 * superseded mid-flight (a company switch changing a query key) must forward
 * React Query's `signal`.
 *
 * A genuine outage still reports — no signal, a signal that is not aborted, or
 * any failure that carried a response falls through this predicate, unless
 * `isUnreportableTransportFailure` then attributes it to page teardown or lost
 * connectivity. The one deliberate trade
 * is a real *network* failure whose caller unmounts in the same tick —
 * unactionable by then, and React Query would have aborted it anyway.
 *
 * Local to the reporter rather than folded into `isRequestAbortError`: that
 * predicate has ~40 call sites branching on it for user-facing recovery, and
 * widening it there would change behavior well outside failure reporting.
 */
const isAbortedRequestFailure = (cause: unknown): boolean => {
  if (!isAxiosError(cause)) return false;
  if (cause.code === "ERR_CANCELED") return true;
  // Gate on the absence of a response: a server that answered produced a real
  // HTTP failure the abort merely followed (a 5xx the caller unmounted on is
  // still a 5xx). Only a response-less failure can be our own cancellation
  // losing the race.
  return cause.response === undefined && cause.config?.signal?.aborted === true;
};

/**
 * The message axios' XHR adapter gives a browser-cancelled request.
 *
 * Verified against **axios 1.19.0**, `lib/adapters/xhr.js:106-111` — a fixed
 * literal in `request.onabort`, under the adapter's own comment "Handle browser
 * request cancellation (as opposed to a manual cancellation)".
 *
 * If an axios upgrade reworded this, `isTimeoutFailure` below would start
 * treating browser aborts as timeouts. That degrades toward *reporting*, so the
 * symptom is teardown noise reappearing inside the `ECONNABORTED` group rather
 * than a lost signal — and no test would catch it, because the tests assert this
 * module's behaviour using this same constant. Re-check this line when bumping
 * axios.
 */
const AXIOS_BROWSER_ABORT_MESSAGE = "Request aborted";

/**
 * Whether a response-less failure is a real timeout rather than a cancellation.
 *
 * `ECONNABORTED` alone cannot answer this: axios' XHR adapter uses that one code
 * for **both** `ontimeout` and `onabort` — and `onabort` is precisely the
 * browser cancelling in-flight requests on unload. The two are distinguishable
 * only by message.
 *
 * So this keys on the abort message rather than on the timeout message, even
 * though testing for a timeout is what it is named for. The abort text is a
 * fixed literal, whereas the timeout text is configurable per request
 * (`timeoutErrorMessage`) and varies with whether a timeout was set. Matching
 * the stable string and defaulting to "timeout" fails toward reporting, which is
 * the safe direction for a signal this class exists to preserve.
 */
const isTimeoutFailure = (error: AxiosError): boolean => {
  // axios uses ETIMEDOUT instead when `transitional.clarifyTimeoutError` is set,
  // in which case ECONNABORTED means an abort unambiguously.
  if (error.code === "ETIMEDOUT") return true;
  return (
    error.code === "ECONNABORTED" &&
    error.message !== AXIOS_BROWSER_ABORT_MESSAGE
  );
};

/**
 * A response-less failure the browser caused, not the API.
 *
 * Page teardown kills every in-flight XHR at once, and axios surfaces each as a
 * bare `ERR_NETWORK`; so does a request issued with no connectivity. Neither is
 * a fact about the API, and both arrive in bursts — one teardown fans a single
 * non-event out across every endpoint the page had in flight.
 *
 * Gated on the absence of a response for the same reason `isAbortedRequestFailure`
 * is: a server that answered produced a real HTTP failure that the teardown
 * merely followed. **A 5xx the user closed the tab on is still a 5xx.**
 *
 * Timeouts are excluded for the same kind of reason. A teardown or a dead
 * interface makes a request fail *immediately*; neither can manufacture the 30s
 * of elapsed clock that `apiClient`'s timeout measures. So a timeout landing
 * inside the teardown window is a coincidence of timing, not a consequence of
 * it — the connection was established and the server did not answer, which is
 * the backend regression this reporter deliberately keeps in its own group.
 */
const isUnreportableTransportFailure = (cause: unknown): boolean => {
  if (!isAxiosError(cause) || cause.response !== undefined) return false;
  if (isTimeoutFailure(cause)) return false;
  return isPageUnloading() || isBrowserOffline();
};

/**
 * Which third party a failure belongs to, or `null` for our own API.
 *
 * The reporter is attached to the global axios instance, so it also sees
 * third-party requests made directly through it. Diagnostics are deliberately
 * path-only, so the hostname has to be carried separately or the event cannot
 * be attributed at all.
 */
export interface ApiErrorReportingDependencies {
  captureException: typeof Sentry.captureException;
  logger: Pick<typeof safeConsole, "error">;
  resolveThirdPartyVendor: typeof resolveThirdPartyVendor;
}

const defaultApiErrorReportingDependencies: ApiErrorReportingDependencies = {
  captureException: Sentry.captureException,
  logger: safeConsole,
  resolveThirdPartyVendor,
};

const getThirdPartyErrorVendor = (
  cause: unknown,
  dependencies: ApiErrorReportingDependencies,
): string | null =>
  isAxiosError(cause)
    ? dependencies.resolveThirdPartyVendor(
        cause.config?.url,
        cause.config?.baseURL,
      )
    : null;

/**
 * Report the user-impacting boundary where a non-auth API 401 has exhausted
 * silent recovery and is about to clear the session and redirect to login.
 *
 * Ordinary 4xx responses remain breadcrumb-only in `reportApiError`. This is a
 * separate exception because the redirect is disruptive and needs to be
 * discoverable in Sentry. Only the normalized route family is retained: raw
 * URLs, request/response bodies, headers, and tenant identifiers never enter
 * the event.
 */
export const reportTerminalAuthFailure = (
  cause: unknown,
  outcome: TerminalAuthRecoveryOutcome,
  dependencies: ApiErrorReportingDependencies = defaultApiErrorReportingDependencies,
): void => {
  try {
    const failure = getSafeAxiosDiagnostics(cause);
    if (failure?.status !== 401) return;

    const routeFamily = toRouteFamily(failure.path);
    const safeError = new Error(
      `Authentication recovery failed (${outcome}): ${failure.method} ${routeFamily}`,
    );
    safeError.name = "AuthSessionRecoveryError";
    dependencies.captureException(safeError, {
      contexts: {
        auth_recovery: {
          code: failure.code,
          method: failure.method,
          outcome,
          route_family: routeFamily,
          status: "401",
        },
      },
      fingerprint: [
        "auth-session-recovery-failed",
        outcome,
        failure.method,
        routeFamily,
      ],
      tags: {
        api_error_code: failure.code,
        api_method: failure.method,
        api_route_family: routeFamily,
        api_status: "401",
        auth_recovery_outcome: outcome,
      },
    });
  } catch {
    dependencies.logger.error("api-error-reporting-failed");
  }
};

export const reportApiError = (
  cause: unknown,
  dependencies: ApiErrorReportingDependencies = defaultApiErrorReportingDependencies,
): void => {
  try {
    if (
      isRequestAbortError(cause) ||
      isAbortedRequestFailure(cause) ||
      isUnreportableTransportFailure(cause)
    ) {
      // Ahead of the `reportedErrors` marking and the console call on purpose:
      // a teardown produces no console line, no breadcrumb and no capture, which
      // is the same total silence a clean cancellation already produces.
      //
      // Deliberately NOT marked in `reportedErrors`. The guards are idempotent,
      // and marking would make suppression sticky — an error suppressed while
      // the page looked offline would stay suppressed after connectivity
      // returned, even on a later pass through a different reporting path.
      return;
    }
    if (hasObjectType(cause) && cause !== null) {
      if (reportedErrors.has(cause)) return;
      reportedErrors.add(cause);
    }
    const failure = getSafeAxiosDiagnostics(cause);
    if (!failure) {
      dependencies.logger.error(
        "api-request-unexpected-error",
        getSafeErrorDiagnostics(cause),
      );
      return;
    }

    dependencies.logger.error("api-request-failed", failure);

    // 4xx are expected control flow whoever they came from, and never captured.
    // Returning before the summary keeps the route-family regexes off the one
    // status class that reaches this reporter in bulk.
    if (failure.status !== null && failure.status < 500) return;

    const vendor = getThirdPartyErrorVendor(cause, dependencies);
    const routeFamily = toRouteFamily(failure.path);
    const summary = {
      code: failure.code,
      method: failure.method,
      outcome: failure.outcome,
      path: failure.path,
      route_family: routeFamily,
    };

    if (vendor === null) {
      // Ours — including a URL we could not resolve, which the origin gate
      // attributes to us on purpose so client-side bugs stay visible.
      const status = String(failure.status ?? "network");

      // A response-less own-API failure that survived the guards above. It
      // still means "this user could not reach us", but it is dominated by
      // conditions we cannot observe from the browser — captive portals, dead
      // VPNs, a teardown a fraction outside the attribution window. Grouping it
      // per route family turned one such event into ~15 issues, which is what
      // buried the real 5xx.
      //
      // So: one group, at `warning`. It still spikes visibly if users genuinely
      // cannot reach the API — that is what the rate-based alert watches — but
      // it is out of error-level alerting. Route family stays in tags and
      // context for drill-down, just not in the group key.
      if (failure.status === null) {
        // Split on the error code alone. `apiClient` sets a 30s timeout, and a
        // timeout also rejects response-less — but it means the connection WAS
        // established and the server did not answer, which is a backend
        // regression worth seeing rather than teardown noise to bury.
        // `failure.code` is bounded by `SAFE_ERROR_CODE_PATTERN` (else
        // "unknown"), so it is safe in a fingerprint.
        const safeError = new Error(
          // The code is repeated in the message because Sentry titles an issue
          // from its first event's exception value. It is part of the
          // fingerprint, so it cannot vary within a group — the title stays
          // stable while the ERR_NETWORK and ECONNABORTED groups stay tellable
          // apart in the issue list.
          `API unreachable (${failure.code}): no response received`,
        );
        safeError.name = "ApiUnreachableError";
        dependencies.captureException(safeError, {
          contexts: { api_request: { ...summary, status } },
          fingerprint: ["api-unreachable", failure.code],
          level: "warning",
          tags: {
            api_error_code: failure.code,
            api_method: failure.method,
            api_route_family: routeFamily,
            api_status: status,
          },
        });
        return;
      }

      const safeError = new Error(
        `API request failed (${status}): ${failure.method} ${failure.path}`,
      );
      safeError.name = "ApiRequestError";
      dependencies.captureException(safeError, {
        contexts: { api_request: { ...summary, status } },
        // Method + route family + status + error code, so unrelated endpoints
        // stop sharing one issue while different customer/database IDs on the
        // same endpoint still group together. Every element is bounded and
        // already sanitized.
        fingerprint: [
          "api-error",
          failure.method,
          routeFamily,
          status,
          failure.code,
        ],
        // Same tag set as the unreachable branch, so a saved search on
        // `api_method` or `api_error_code` spans both rather than silently
        // returning only one class.
        tags: {
          api_error_code: failure.code,
          api_method: failure.method,
          api_route_family: routeFamily,
          api_status: status,
        },
      });
      return;
    }

    // A third party that answered and said it was broken. Gated on a real
    // response because a response-less third-party failure is indistinguishable
    // from an ad blocker or extension blocking the host outright — both surface
    // as a bare `ERR_NETWORK`, and that ambiguity cannot be resolved in the
    // browser. Those stay breadcrumb-only.
    //
    // Reported at `warning` so it records without tripping error-level alerting:
    // it explains an outage after the fact, it does not detect one (we only see
    // vendors our users happen to call). Never an `ApiRequestError` — that name
    // has to keep meaning that our own API is broken.
    if (failure.status !== null && failure.status >= 500) {
      const status = String(failure.status);
      // The vendor may be an unregistered hostname rather than a known label,
      // and a hostname can carry user-influenced data.
      const safeVendor = sanitizeDiagnosticString(vendor);
      const safeError = new Error(
        `Third-party request failed (${status}): ${safeVendor} ${failure.method} ${failure.path}`,
      );
      safeError.name = "ThirdPartyRequestError";
      dependencies.captureException(safeError, {
        contexts: {
          third_party_request: { ...summary, status, vendor: safeVendor },
        },
        fingerprint: ["third-party-error", safeVendor, routeFamily, status],
        level: "warning",
        tags: { api_status: status, third_party_vendor: safeVendor },
      });
    }
  } catch {
    dependencies.logger.error("api-error-reporting-failed");
  }
};

/**
 * One response interceptor that reports the failure and rejects unchanged.
 * Attach after any recovery interceptors (silent-auth retry).
 */
export const attachApiErrorReporting = (
  client: AxiosInstance,
  dependencies: ApiErrorReportingDependencies = defaultApiErrorReportingDependencies,
): void => {
  client.interceptors.response.use(
    undefined,
    function reportApiResponseError(cause: unknown) {
      reportApiError(cause, dependencies);
      return Promise.reject(cause);
    },
  );
};
